import { useState, useEffect, useCallback } from "react";
import { Plus, Pencil, Trash2 } from "lucide-react";
import axiosInstance from "../../../API/interceptor";
import Swal from "sweetalert2";
import { CONFIRM_CLASSES, INPUT_CLASSES, successToast, errorToast } from "../swalTheme";
import PaginationBar from "../components/PaginationBar";

const PAGE_SIZE = 10;

// High z-index for Swal to avoid settings page z-index conflicts
const SWAL_OPTS = {
  customClass: { container: "swal-priority-container" },
};

const PRIORITY_COLORS = {
  LOW:      { bg: "#dcfce7", color: "#15803d", dot: "#22c55e" },
  MEDIUM:   { bg: "#fef9c3", color: "#a16207", dot: "#eab308" },
  HIGH:     { bg: "#ffedd5", color: "#c2410c", dot: "#f97316" },
  URGENT:   { bg: "#fee2e2", color: "#b91c1c", dot: "#ef4444" },
  CRITICAL: { bg: "#f3e8ff", color: "#7e22ce", dot: "#a855f7" },
};

function getPriorityStyle(priority) {
  return PRIORITY_COLORS[priority?.toUpperCase()] || { bg: "#f1f5f9", color: "#475569", dot: "#94a3b8" };
}

export default function SetPriorityTab() {
  const [priorities, setPriorities] = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [search,     setSearch]     = useState("");
  const [page,       setPage]       = useState(1);
  const [pageSize,   setPageSize]   = useState(PAGE_SIZE);

  const loadPriorities = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.post("/task_assignment/task_priority");
      setPriorities(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Failed to load priorities:", err);
      errorToast.fire({ icon: "error", title: "Failed to load priorities" });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadPriorities(); }, [loadPriorities]);

  async function addPriority() {
    const { value: priority, isConfirmed } = await Swal.fire({
      ...SWAL_OPTS,
      title: "Enter Priority",
      input: "text",
      inputAttributes: { autocapitalize: "off", placeholder: "e.g. HIGH" },
      showCancelButton: true,
      reverseButtons: true,
      confirmButtonText: "Add",
      cancelButtonText: "Cancel",
      buttonsStyling: false,
      customClass: { ...SWAL_OPTS.customClass, ...INPUT_CLASSES },
      showLoaderOnConfirm: true,
      preConfirm: (val) => {
        if (!val?.trim()) { Swal.showValidationMessage("Priority cannot be empty"); return false; }
        return axiosInstance.post("/task_assignment/add_priority", { priority: val.trim() })
          .then(res => res.data)
          .catch(err => { Swal.showValidationMessage(err.response?.data?.error || err.message); });
      },
      allowOutsideClick: () => !Swal.isLoading(),
    });

    if (!isConfirmed) return;

    // Reload list FIRST, then show success
    await loadPriorities();
    successToast.fire({ icon: "success", title: "Priority added!" });
  }

  async function editPriority(slNo, currentValue) {
    const { isConfirmed } = await Swal.fire({
      ...SWAL_OPTS,
      title: "Edit Priority",
      input: "text",
      inputValue: currentValue,
      inputAttributes: { autocapitalize: "off" },
      showCancelButton: true,
      reverseButtons: true,
      confirmButtonText: "Update",
      cancelButtonText: "Cancel",
      buttonsStyling: false,
      customClass: { ...SWAL_OPTS.customClass, ...INPUT_CLASSES },
      showLoaderOnConfirm: true,
      preConfirm: (newPriority) => {
        if (!newPriority?.trim()) { Swal.showValidationMessage("Priority cannot be empty"); return false; }
        return axiosInstance.put(`/task_assignment/update_priority/${slNo}`, { priority: newPriority.trim() })
          .then(res => res.data)
          .catch(err => { Swal.showValidationMessage(err.response?.data?.error || err.message); });
      },
      allowOutsideClick: () => !Swal.isLoading(),
    });

    if (!isConfirmed) return;

    await loadPriorities();
    successToast.fire({ icon: "success", title: "Priority updated!" });
  }

  async function deletePriority(slNo) {
    const { isConfirmed } = await Swal.fire({
      ...SWAL_OPTS,
      title: "Are you sure?",
      text: "You will not be able to recover this priority!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "No, cancel!",
      reverseButtons: true,
      buttonsStyling: false,
      customClass: { ...SWAL_OPTS.customClass, ...CONFIRM_CLASSES },
    });

    if (!isConfirmed) return;

    try {
      await axiosInstance.delete(`/task_assignment/delete_priority/${slNo}`);
      await loadPriorities();
      successToast.fire({ icon: "success", title: "Deleted!" });
    } catch (err) {
      errorToast.fire({ icon: "error", title: "Error!", text: err.message });
    }
  }

  const filtered   = priorities.filter(p =>
    p.priority?.toLowerCase().includes(search.toLowerCase()) ||
    String(p.sl_no).includes(search)
  );
  const paginated  = filtered.slice((page - 1) * pageSize, page * pageSize);
  function handleSearch(val) { setSearch(val); setPage(1); }

  return (
    <div>
      {/* Swal z-index fix */}
      <style>{`.swal-priority-container { z-index: 99999 !important; }`}</style>

      {/* Search + Add button */}
      <div className="flex items-center justify-between gap-3 mb-5">
        <div className="flex items-center gap-2 text-sm text-slate-500">
          Search:
          <input
            className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm outline-none focus:ring-2 focus:ring-indigo-400 transition-all w-[200px]"
            value={search}
            onChange={e => handleSearch(e.target.value)}
          />
        </div>
        <button
          onClick={addPriority}
          className="flex items-center gap-2 px-5 py-2.5 text-sm font-bold text-white rounded-xl cursor-pointer border-none hover:opacity-90 transition-all bg-[#10B981] shadow-[0_4px_14px_rgba(16,185,129,.35)]"
        >
          <Plus size={15} /> Add Priority
        </button>
      </div>

      {/* Table */}
      <div className="rounded-xl border border-gray-100 overflow-hidden shadow-[0_1px_8px_rgba(0,0,0,0.05)]">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50">
              <th className="px-6 py-3.5 text-left text-xs font-bold text-slate-500 uppercase tracking-wider w-24">Sl No</th>
              <th className="px-6 py-3.5 text-left text-xs font-bold text-slate-500 uppercase tracking-wider">Priority</th>
              <th className="px-6 py-3.5 text-left text-xs font-bold text-slate-500 uppercase tracking-wider w-28">Action</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              [...Array(5)].map((_, i) => (
                <tr key={i} className="border-t border-gray-50">
                  <td className="px-6 py-4"><div className="h-4 w-6 bg-gray-100 rounded animate-pulse" /></td>
                  <td className="px-6 py-4"><div className="h-6 w-24 bg-gray-100 rounded-full animate-pulse" /></td>
                  <td className="px-6 py-4"><div className="h-7 w-16 bg-gray-100 rounded animate-pulse" /></td>
                </tr>
              ))
            ) : paginated.length === 0 ? (
              <tr>
                <td colSpan={3} className="text-center py-14 text-slate-400 text-sm">
                  <div className="flex flex-col items-center gap-2">
                    <span className="text-2xl">📋</span>
                    <span>No priorities found</span>
                  </div>
                </td>
              </tr>
            ) : paginated.map((item, idx) => {
              const style  = getPriorityStyle(item.priority);
              const isEven = idx % 2 === 0;
              return (
                <tr
                  key={item.sl_no}
                  className={`border-t border-gray-50 transition-colors hover:bg-slate-50 ${isEven ? "bg-white" : "bg-[#fafbff]"}`}
                >
                  <td className="px-6 py-4">
                    <span className="text-xs font-mono text-slate-400">{item.sl_no}</span>
                  </td>
                  <td className="px-6 py-4">
                    <span
                      className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold"
                      style={{ background: style.bg, color: style.color }}
                    >
                      <span className="w-1.5 h-1.5 rounded-full" style={{ background: style.dot }} />
                      {item.priority}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-1.5">
                      <button
                        onClick={() => editPriority(item.sl_no, item.priority)}
                        className="p-1.5 rounded-lg text-white transition-all hover:opacity-80 active:scale-95 bg-[#6c5ffc]"
                        title="Edit"
                      >
                        <Pencil size={12} />
                      </button>
                      <button
                        onClick={() => deletePriority(item.sl_no)}
                        className="p-1.5 rounded-lg text-white transition-all hover:opacity-80 active:scale-95 bg-red-500"
                        title="Delete"
                      >
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Footer */}
      <PaginationBar
        page={page}
        pageSize={pageSize}
        setPage={setPage}
        setPageSize={setPageSize}
        totalRecords={filtered.length}
      />
    </div>
  );
}